import {Button} from '@primer/react'
import {type ReactNode, useCallback, useEffect, useId, useRef, useState} from 'react'
import styles from './Stackable.module.css'

const MAX_VISIBLE = 5

export interface StackableProps {
  children?: ReactNode[]
}

export function Stackable({children}: StackableProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const stackId = useId()
  const toggleRef = useRef<HTMLButtonElement | null>(null)
  // Set when a dismissal causes focus loss; the next render restores focus to a
  // sensible target (next alive card, then the toggle).
  const pendingFocusAfterDismissRef = useRef(false)
  const childrenArray = Array.isArray(children) ? children : children != null ? [children] : []

  // Track which original-index children have rendered nothing (dismissed themselves).
  const [dismissed, setDismissed] = useState<ReadonlySet<number>>(() => new Set())

  const handleDismissedChange = useCallback((index: number, isDismissed: boolean, hadFocus: boolean) => {
    if (isDismissed && hadFocus) {
      pendingFocusAfterDismissRef.current = true
    }
    setDismissed(prev => {
      if (prev.has(index) === isDismissed) return prev
      const next = new Set(prev)
      if (isDismissed) next.add(index)
      else next.delete(index)
      return next
    })
  }, [])

  const consumePendingFocus = useCallback(() => {
    if (!pendingFocusAfterDismissRef.current) return false
    pendingFocusAfterDismissRef.current = false
    return true
  }, [])

  if (childrenArray.length === 0) {
    return null
  }

  const aliveIndices = childrenArray.map((_, i) => i).filter(i => !dismissed.has(i))

  if (aliveIndices.length === 0) {
    return null
  }

  // When collapsed, only the anchor and a small peek stack need to exist. When
  // expanded, render every alive card so none are hidden by the cap.
  const visibleIndices = isOpen ? aliveIndices : aliveIndices.slice(0, MAX_VISIBLE)
  const [anchorIndex, ...stackedIndices] = visibleIndices

  if (anchorIndex === undefined) {
    return null
  }

  const anchor = (
    <DismissSlot
      key={anchorIndex}
      index={anchorIndex}
      onDismissedChange={handleDismissedChange}
      consumePendingFocus={consumePendingFocus}
      toggleRef={toggleRef}
    >
      {childrenArray[anchorIndex]}
    </DismissSlot>
  )

  if (stackedIndices.length === 0) {
    return anchor
  }

  return (
    <div
      className={[styles.Stackable, isOpen ? '' : styles.isClosed, !isOpen && isHovered ? styles.isHovered : '']
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.Item}>{anchor}</div>
      <div id={stackId} className={styles.Stack} inert={!isOpen}>
        <div className={styles.StackInner}>
          {stackedIndices.map(i => (
            <div key={i} className={styles.Item}>
              <DismissSlot
                index={i}
                onDismissedChange={handleDismissedChange}
                consumePendingFocus={consumePendingFocus}
                toggleRef={toggleRef}
              >
                {childrenArray[i]}
              </DismissSlot>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Button
          ref={toggleRef}
          onClick={() => setIsOpen(open => !open)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          variant="invisible"
          aria-controls={stackId}
          aria-expanded={isOpen}
        >
          {isOpen ? 'Show fewer recommendations' : 'Show more recommendations'}
        </Button>
      </div>
    </div>
  )
}

interface DismissSlotProps {
  index: number
  onDismissedChange: (index: number, isDismissed: boolean, hadFocus: boolean) => void
  consumePendingFocus: () => boolean
  toggleRef: React.RefObject<HTMLButtonElement | null>
  children: ReactNode
}

// Reports up to the parent when a child renders no DOM (e.g. a nudge card that
// returns null after being dismissed) so the parent can promote the next card
// and hide the expand toggle when nothing is left.
function DismissSlot({index, onDismissedChange, consumePendingFocus, toggleRef, children}: DismissSlotProps) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let hadFocus = el.contains(document.activeElement)
    const onFocusIn = () => {
      hadFocus = true
    }
    const onFocusOut = (event: FocusEvent) => {
      // relatedTarget is the new focus target. If it's still inside this slot,
      // focus hasn't actually left.
      const next = event.relatedTarget as Node | null
      if (!next || !el.contains(next)) {
        hadFocus = false
      }
    }
    el.addEventListener('focusin', onFocusIn)
    el.addEventListener('focusout', onFocusOut)

    const check = () => {
      const isEmpty = el.children.length === 0
      // When the slot just became empty, `hadFocus` still reflects the state
      // immediately before the dismissal because the removed element's
      // focusout does not fire (the element is gone, not just blurred).
      const lostFocus = isEmpty && hadFocus
      if (isEmpty) hadFocus = false
      onDismissedChange(index, isEmpty, lostFocus)
    }
    check()

    const observer = new MutationObserver(check)
    observer.observe(el, {childList: true})
    return () => {
      observer.disconnect()
      el.removeEventListener('focusin', onFocusIn)
      el.removeEventListener('focusout', onFocusOut)
    }
  }, [index, onDismissedChange])

  // After this slot is promoted/rendered following a focus-losing dismissal,
  // move focus into the first focusable descendant. Falls back to the toggle.
  useEffect(() => {
    if (!consumePendingFocus()) return
    const el = ref.current
    const focusTarget =
      el?.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])',
      ) ?? toggleRef.current
    focusTarget?.focus()
  })

  return (
    <div ref={ref} className={styles.Slot}>
      {children}
    </div>
  )
}
